// fix/pwa-error-boundary -- global chunk-load failure banner.
//
// Async dynamic-import rejections don't bubble through React error
// boundaries (the boundary only catches render/lifecycle exceptions
// from inside its child tree). A failed lazy() import surfaces as a
// window-level unhandledrejection, which is why we also listen at the
// window scope here.
//
// On a matching event we set state.show -> banner renders at the top
// of the viewport with a Reload button (real <button>, not a div).
// The user must click -- we never auto-reload (spec: "Auto-reload
// loops are a known footgun").

import { useEffect, useReducer, useState } from "react";
import { tokens, subscribe } from "./cf_theme.js";
import { isChunkLoadError } from "./error_handling.js";

export default function ChunkLoadBanner() {
  const [show, setShow] = useState(false);
  // re-render on theme change so the banner repaints in the new palette
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribe(() => bump()), []);

  useEffect(() => {
    // Async dynamic-import failures surface as Promise rejections.
    const onReject = (e) => {
      try { if (isChunkLoadError(e && e.reason)) setShow(true); } catch {}
    };
    // Synchronous script errors (rare for chunk-loads but possible
    // when index.html's <script type=module> tag itself 404s).
    const onErr = (e) => {
      try {
        const fromErr  = e && e.error ? isChunkLoadError(e.error) : false;
        const fromMsg  = e && e.message ? isChunkLoadError({ message: e.message }) : false;
        if (fromErr || fromMsg) setShow(true);
      } catch {}
    };
    window.addEventListener("unhandledrejection", onReject);
    window.addEventListener("error", onErr);
    return () => {
      window.removeEventListener("unhandledrejection", onReject);
      window.removeEventListener("error", onErr);
    };
  }, []);

  if (!show) return null;
  const t = tokens();
  const onReload = () => { try { window.location.reload(); } catch {} };

  return (
    <div role="alert" aria-live="assertive" className="cf-slide-in-top" style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 99999,
      background: t.accent, color: t.accent_fg,
      padding: "10px 16px",
      display: "flex", alignItems: "center", gap: 12,
      fontFamily: "Outfit, system-ui, sans-serif",
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    }}>
      <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
        A new version of the app is available. Reload to continue.
      </div>
      <button
        type="button"
        onClick={onReload}
        autoFocus
        style={{
          background: t.accent_fg, color: t.accent,
          border: "none", borderRadius: 7,
          padding: "6px 14px", fontSize: 12, fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit",
        }}>
        Reload
      </button>
      <button
        type="button"
        onClick={() => setShow(false)}
        aria-label="Dismiss new-version banner"
        style={{
          background: "transparent",
          border: `1px solid ${t.accent_fg}55`,
          color: t.accent_fg,
          borderRadius: 7,
          padding: "4px 8px", fontSize: 11,
          cursor: "pointer", fontFamily: "inherit",
        }}>
        ✕
      </button>
    </div>
  );
}
